import type { UseFormReturn } from 'react-hook-form';
import { Section } from '../../../components/ui/Section';
import { Badge } from '../../../components/ui/Badge';
import { asRecord, str } from '../../../platform/api/utils';
import {
  type MovementForm,
  MOVEMENT_TYPE_LABELS,
  MOVEMENT_TYPE_VARIANTS,
  Field,
  inputCss,
  primaryBtn,
  secondaryBtn,
  modalOverlay,
} from './inventory-detail.support';

function fmtDate(v: unknown): string {
  if (typeof v === 'string' && v.length >= 10) return v.substring(0, 10);
  if (v instanceof Date) return v.toISOString().substring(0, 10);
  return '—';
}
export function InventoryMovementsTab(props: { movements: Record<string, unknown>[] }) {
  const { movements } = props;
  return (
    <Section title="Histórico de movimentações">
      {movements.length === 0 ? (
        <p style={{ color: 'var(--faint)', fontSize: '0.875rem' }}>
          Nenhuma movimentação registrada.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {movements.map((raw) => {
            const m = asRecord(raw);
            const mtype = str(m.type);
            return (
              <div
                key={str(m.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.45rem 0.75rem',
                  borderRadius: 8,
                  background: 'var(--paper)',
                  border: '1px solid var(--line-soft)',
                }}
              >
                <Badge
                  label={MOVEMENT_TYPE_LABELS[mtype] ?? mtype}
                  variant={MOVEMENT_TYPE_VARIANTS[mtype] ?? 'default'}
                />
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color:
                      mtype === 'OUTGOING' || mtype === 'LOSS_DISPOSAL'
                        ? 'var(--danger)'
                        : 'var(--success)',
                  }}
                >
                  {mtype === 'OUTGOING' || mtype === 'LOSS_DISPOSAL' ? '-' : '+'}
                  {str(m.quantity)}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--faint)' }}>
                  {fmtDate(m.createdAt)}
                </span>
                {str(m.reason) && (
                  <span style={{ fontSize: '0.8rem', color: 'var(--muted)', marginLeft: 'auto' }}>
                    {str(m.reason)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
export function InventoryMovementModal(props: {
  it: Record<string, unknown>;
  mvForm: UseFormReturn<MovementForm>;
  movementError: string;
  createMovement: { isPending: boolean };
  onClose: () => void;
  onSubmit: () => void;
}) {
  const { it, mvForm, movementError, createMovement, onClose, onSubmit } = props;
  return (
    <div
      style={modalOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: '1.5rem',
          minWidth: 380,
          maxWidth: 440,
        }}
      >
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>Nova movimentação</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: 'var(--muted)' }}>
          Saldo atual: <strong>{str(it.currentQuantity)}</strong> {str(it.unit)}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Field label="Tipo">
            <select {...mvForm.register('type')} style={inputCss}>
              {Object.entries(MOVEMENT_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Quantidade" error={mvForm.formState.errors.quantity?.message}>
            <input
              {...mvForm.register('quantity')}
              style={inputCss}
              type="number"
              step="any"
              min="0"
            />
          </Field>
          <Field label="Motivo">
            <input {...mvForm.register('reason')} style={inputCss} placeholder="Opcional" />
          </Field>
          {movementError && (
            <p
              style={{
                color: 'var(--danger)',
                fontSize: '0.8rem',
                margin: 0,
                padding: '0.5rem 0.65rem',
                background: 'var(--danger-soft)',
                borderRadius: 10,
                border: '1px solid var(--danger-soft)',
              }}
            >
              {movementError}
            </p>
          )}
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              justifyContent: 'flex-end',
              marginTop: '0.5rem',
            }}
          >
            <button type="button" onClick={onClose} style={secondaryBtn}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={createMovement.isPending}
              style={primaryBtn}
            >
              {createMovement.isPending ? 'Registrando...' : 'Registrar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
